// BabyAI client — calls the demo's Pages Function proxy at
// /api/babyai/v1/chat/completions, which attaches the deploy's
// HF_TOKEN server-side and forwards to the HF Space. Same-origin,
// no BYOK required.
//
// Mirrors agicore-foundry's worker-proxy pattern: the user opens the
// page and the AI responder is alive immediately. The HF token never
// reaches the browser.
//
// In local development (`npm run dev`) the Pages Function isn't
// served by Vite, so calls 404. Local dev runs in stub mode by
// design; use `wrangler pages dev` if you need the AI path locally.

const BABYAI_ENDPOINT = "/api/babyai/v1/chat/completions";

export interface BabyAIMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface BabyAICallResult {
  text: string;
  /** Which underlying model in the MoE answered. Surfaced for the
   *  AI Thinking panel ("Qwen3-Coder 30B-A3B picked this rule…"). */
  model: string;
  raw: unknown;
}

export interface BabyAICallOptions {
  modelId?: string;
  messages: BabyAIMessage[];
  maxTokens?: number;
  temperature?: number;
}

export class BabyAIError extends Error {
  status: number;
  body: string;
  constructor(message: string, status: number, body: string) {
    super(`${message} (HTTP ${status}): ${body.slice(0, 200)}`);
    this.name = "BabyAIError";
    this.status = status;
    this.body = body;
  }
}

// Default to the coder model explicitly. The MoE keeps routing to
// Qwen3-8B (a thinking-mode model) which produces empty content for
// our structured-JSON prompt — it spends the budget thinking and
// emits nothing in the final-answer slot. Qwen3-Coder-30B-A3B is
// designed for structured output and gives us reliable JSON.
// Available alternatives if this one underperforms:
//   Qwen/Qwen2.5-Coder-7B-Instruct  — smaller coder, faster
//   zai-org/GLM-4.7-Flash           — good generalist
//   meta-llama/Llama-3.1-8B-Instruct — fast lane fallback
const DEFAULT_MODEL = "Qwen/Qwen3-Coder-30B-A3B-Instruct";

export async function callBabyAI(opts: BabyAICallOptions): Promise<BabyAICallResult> {
  const body = {
    model:       opts.modelId ?? DEFAULT_MODEL,
    messages:    opts.messages,
    // 2048 leaves room for thinking-mode models (Qwen3-8B et al) that
    // burn budget on reasoning before producing a final answer. 512
    // was tight enough that thinking models stopped before emitting
    // the JSON body — surfaced as content:null.
    max_tokens:  opts.maxTokens ?? 2048,
    temperature: opts.temperature ?? 0.4,
    stream:      false,
  };
  const resp = await fetch(BABYAI_ENDPOINT, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "Accept": "application/json" },
    body:    JSON.stringify(body),
  });
  const text = await resp.text();
  if (!resp.ok) throw new BabyAIError("BabyAI call failed", resp.status, text);
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new BabyAIError("BabyAI returned non-JSON", resp.status, text);
  }
  const r = raw as {
    choices?: Array<{
      message?: {
        // OpenAI standard
        content?: string | null;
        // Qwen3 / DeepSeek-style thinking models surface their chain
        // of thought here when the final-answer slot is empty. Last-
        // resort source for JSON extraction.
        reasoning_content?: string | null;
        reasoning?: string | null;
      };
    }>;
    model?: string;
  };
  const msg = r.choices?.[0]?.message;
  // Prefer content; fall back to reasoning fields when the model
  // finished without producing a separate final answer.
  const content =
    (typeof msg?.content === "string" && msg.content.length > 0 ? msg.content : null) ??
    (typeof msg?.reasoning_content === "string" && msg.reasoning_content.length > 0 ? msg.reasoning_content : null) ??
    (typeof msg?.reasoning === "string" && msg.reasoning.length > 0 ? msg.reasoning : null);
  if (content === null) {
    throw new BabyAIError(
      "BabyAI response has empty content + no reasoning fallback",
      resp.status,
      text,
    );
  }
  return { text: content, model: r.model ?? "babyai", raw };
}
